import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

const isValidPostcode = (pc) => /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(pc.trim());
const money = (v) => (v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(2));

export default function ParkingVsTaxiT5() {
  const [form, setForm] = useState({
    pickup_postcode: "",
    dropoff_terminal: "LHR5",
    passengers: 2,
    luggage: 2,
    days_parked: 3,
    pickup_datetime: ""
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSubmitted = useRef(false);

  useEffect(() => {
    const nextWeekdayMorning = () => {
      const now = new Date();
      const day = now.getDay();
      const daysToMon = (8 - day) % 7 || 1;
      const target = new Date(now);
      target.setDate(now.getDate() + daysToMon);
      target.setHours(9, 0, 0, 0);
      const pad = (n) => String(n).padStart(2, "0");
      return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
    };

    setForm((prev) => ({
      ...prev,
      pickup_postcode: "SW1A 1AA",
      dropoff_terminal: "LHR5",
      passengers: 2,
      luggage: 2,
      days_parked: 3,
      pickup_datetime: nextWeekdayMorning()
    }));
  }, []);

  const submit = async () => {
    const pc = form.pickup_postcode.trim();
    if (!pc || !isValidPostcode(pc)) {
      setError("Please enter a valid UK pickup postcode.");
      return;
    }
    if (!form.pickup_datetime) {
      setError("Please select a pickup date and time.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...form,
        pickup_postcode: pc.toUpperCase(),
        dropoff_terminal: "LHR5",
        passengers: Math.min(6, Math.max(1, Number(form.passengers) || 2)),
        luggage: Math.min(6, Math.max(0, Number(form.luggage) || 2)),
        days_parked: Math.min(10, Math.max(3, Number(form.days_parked) || 3))
      };
      const res = await api.post("/api/parking/compare", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate comparison. Please try again.");
    }

    setLoading(false);
  };

  useEffect(() => {
    if (autoSubmitted.current) return;
    if (form.pickup_postcode && form.pickup_datetime) {
      autoSubmitted.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup_postcode, form.pickup_datetime]);

  const parkingTotal = result?.costs?.parking?.total ?? null;
  const taxiTwoWay = result?.costs?.taxi?.cheapcabgrab_two_way ?? null;
  const parkingAvailable = parkingTotal != null && Number.isFinite(Number(parkingTotal));
  const delta = parkingTotal != null && taxiTwoWay != null ? Number((parkingTotal - taxiTwoWay).toFixed(2)) : null;
  const heroVerdict = useMemo(() => {
    if (delta == null) return "Compare complete for Terminal 5.";
    if (Math.abs(delta) < 10) {
      return "Costs are similar for this T5 trip — choose the option that reduces stress and fatigue.";
    }
    if (delta < 0) {
      return "✅ Best choice for Terminal 5: Parking is cheaper for your inputs. Taxi still removes post-flight driving and T5 parking anxiety.";
    }
    return "🚖 Best choice for Terminal 5: Taxi. Even if parking looks close, T5 short-stay pricing, delays, and fatigue make taxi the safer pick for 24–48 hour trips.";
  }, [delta]);

  const listStyle = { paddingLeft: 18, lineHeight: 1.7, color: "#1f2937", fontSize: 15, marginTop: 6, marginBottom: 6 };
  const sectionTitleStyle = { marginTop: 20, marginBottom: 8, fontSize: 18, fontWeight: 800, color: "#0f172a" };
  const bodyTextStyle = { fontSize: 15, color: "#1f2937", lineHeight: 1.6, marginTop: 4, marginBottom: 4 };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Heathrow Terminal 5 Parking vs Taxi (2026 Cost Comparison)</title>
        <meta
          name="description"
          content="Terminal 5 parking is the most expensive at Heathrow. Compare T5 Short Stay, Long Stay, and taxi costs — including delays, convenience, and stress — in seconds."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/heathrow-terminal-5-parking-vs-taxi" />
        <meta property="og:title" content="Heathrow Terminal 5 Parking vs Taxi: Which Is Cheaper in 2026?" />
        <meta
          property="og:description"
          content="See whether parking or taxi is better for Terminal 5. Includes Short Stay pain, congestion risk, fatigue, and live taxi fares."
        />
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="parking-header">
        <h1>Heathrow Terminal 5 Parking vs Taxi: Which Is Cheaper in 2026?</h1>
        <p style={{ fontSize: 15, color: "#0f172a", marginBottom: 6 }}>
          Terminal 5 has the highest parking costs at Heathrow. This tool compares cost, stress, and time risk for T5 parking vs taxi with real prices and fatigue factors — so you don’t overpay or stress before your flight.
        </p>
        <p style={{ fontSize: 14, color: "#374151", marginBottom: 0 }}>
          Terminal 5 is locked for this guide. Use the main calculator for other terminals.
        </p>
      </div>

      <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f8fafc" }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>The quick answer for Terminal 5</div>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", marginTop: 8 }}>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
            <div style={{ fontWeight: 800 }}>Use PARKING if:</div>
            <ul style={listStyle}>
              <li>You want your own car on return and accept T5 parking pricing.</li>
              <li>You’re ULEZ-compliant and fine with the return drive.</li>
              <li>You’re price-sensitive and confident about timing.</li>
            </ul>
          </div>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
            <div style={{ fontWeight: 800 }}>Use TAXI if:</div>
            <ul style={listStyle}>
              <li>You want to avoid short-stay overruns and PCN risk.</li>
              <li>You prefer zero post-flight driving fatigue (BA long-haul).</li>
              <li>You value door-to-door over parking shuttles.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="parking-form" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, marginTop: 14 }}>
        <div className="form-group">
          <label>Pickup postcode (editable)</label>
          <input
            className="parking-input"
            value={form.pickup_postcode}
            onChange={(e) => setForm({ ...form, pickup_postcode: e.target.value })}
            placeholder="e.g. SW1A 1AA"
          />
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
            Postcode affects taxi distance. Prices below show a typical Central London example — edit if needed.
          </div>
        </div>

        <div className="form-group">
          <label>Terminal</label>
          <input className="parking-input" value="Terminal 5 (locked for this guide)" disabled />
        </div>

        <div className="form-group">
          <label>Passengers</label>
          <input
            type="number"
            className="parking-input"
            min="1"
            max="6"
            value={form.passengers}
            onChange={(e) => setForm({ ...form, passengers: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Luggage</label>
          <input
            type="number"
            className="parking-input"
            min="0"
            max="6"
            value={form.luggage}
            onChange={(e) => setForm({ ...form, luggage: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Days parked (framing 48 hours)</label>
          <input
            type="number"
            className="parking-input"
            min="3"
            max="10"
            value={form.days_parked}
            onChange={(e) => setForm({ ...form, days_parked: e.target.value })}
          />
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Default is 3 days; parking pricing is most reliable for 3–7 day stays.</div>
        </div>

        <div className="form-group">
          <label>Pickup date & time</label>
          <input
            type="datetime-local"
            className="parking-input"
            value={form.pickup_datetime}
            onChange={(e) => setForm({ ...form, pickup_datetime: e.target.value })}
          />
        </div>

        <button className="parking-btn" onClick={submit}>
          {loading ? "Calculating…" : "Compare parking vs taxi for Terminal 5"}
        </button>

        {error && <p style={{ color: "red", marginTop: 8 }}>{error}</p>}
      </div>

      {result && (
        <div className="parking-results">
          <div
            style={{
              border: "1px solid #16a34a",
              borderRadius: 14,
              padding: 16,
              background: "#ecfdf5",
              boxShadow: "0 8px 20px rgba(22,163,74,0.08)"
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 18 }}>{heroVerdict}</div>
            <div style={{ marginTop: 6, fontSize: 14, color: "#1f2937" }}>
              T5 comparison for {result.journey?.pickup_postcode}. Shows 48-hour style stays, T5 parking, taxi return, and fatigue/convenience indicators.
            </div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 8 }}>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
                <div style={{ fontWeight: 700 }}>Terminal 5 parking total</div>
                <div style={{ fontSize: 18 }}>
                  {parkingAvailable ? money(parkingTotal) : "—"}
                </div>
                {!parkingAvailable && (
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                    Parking price unavailable for this duration; try adjusting days or use the main calculator.
                  </div>
                )}
              </div>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
                <div style={{ fontWeight: 700 }}>Two taxis (return)</div>
                <div style={{ fontSize: 18 }}>{money(taxiTwoWay)}</div>
              </div>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: "#475467" }}>
              Results vary by distance, passengers, and short-stay overruns. Taxi removes post-flight driving, short-stay PCN risk, and shuttle time. Assumes a short ~48-hour T5 stay; longer trips may favour Long Stay parking.
            </div>
          </div>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Terminal 5 parking costs vs taxi</div>
            <table className="parking-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>Option</th>
                  <th>Cost (£)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>🚗 T5 parking total</td>
                  <td>{parkingAvailable ? money(result.costs?.parking?.total) : "—"}</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: 24, color: "#666" }}>└ Parking price ({result.costs?.parking?.pricing_mode || "—"})</td>
                  <td style={{ color: "#666" }}>{parkingAvailable ? money(result.costs?.parking?.parking_price) : "—"}</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: 24, color: "#666" }}>└ Fuel estimate (return driving)</td>
                  <td style={{ color: "#666" }}>{money(result.costs?.parking?.fuel_price)}</td>
                </tr>
                <tr>
                  <td>🚖 CheapCabGrab (2 trips)</td>
                  <td>{money(result.costs?.taxi?.cheapcabgrab_two_way)}</td>
                </tr>
                <tr>
                  <td>📱 Ride-hail range (2 trips)</td>
                  <td>
                    {result.costs?.taxi?.ride_hail_two_way
                      ? `£${money(result.costs.taxi.ride_hail_two_way.min)} – £${money(result.costs.taxi.ride_hail_two_way.max)}`
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <td>🚕 Black cab range (2 trips)</td>
                  <td>
                    {result.costs?.taxi?.black_cab_two_way_range
                      ? `£${money(result.costs.taxi.black_cab_two_way_range.min)} – £${money(result.costs.taxi.black_cab_two_way_range.max)}`
                      : result.costs?.taxi?.black_cab_two_way != null
                        ? `~£${money(result.costs.taxi.black_cab_two_way)}`
                        : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 13, color: "#374151" }}>
              Need other comparisons? <a href="/heathrow-parking-vs-taxi">Main tool</a> • <a href="/7-day-heathrow-parking-vs-taxi">7-day</a> • <a href="/central-london-heathrow-parking-vs-taxi">Central London</a> • <a href="/reading-heathrow-parking-vs-taxi">Reading</a> • <a href="/oxford-heathrow-parking-vs-taxi">Oxford</a> • <a href="/heathrow-dropoff-free">Free drop-off</a>.
            </div>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f9fafb" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Why Terminal 5 feels more stressful</div>
            <ul style={listStyle}>
              <li>Largest terminal at Heathrow; longer internal walks.</li>
              <li>BA long-haul arrivals → higher fatigue post-flight.</li>
              <li>Short Stay prices are the highest; overruns escalate cost quickly.</li>
              <li>Delays mean parking overruns + shuttle time at T5.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>When parking vs taxi at T5</div>
            <div style={{ fontWeight: 700, marginTop: 6 }}>Parking can work if:</div>
            <ul style={listStyle}>
              <li>You’re ULEZ-compliant and accept T5 Short Stay/Long Stay costs.</li>
              <li>You’re confident on timing (≤48 hours) and okay with shuttles.</li>
              <li>You need your car immediately on return.</li>
            </ul>
            <div style={{ fontWeight: 700, marginTop: 10 }}>Taxi is often better if:</div>
            <ul style={listStyle}>
              <li>BA long-haul/overnight arrivals → high fatigue.</li>
              <li>Short Stay overrun anxiety or PCN risk.</li>
              <li>You want door-to-door with no parking admin or shuttle time.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f8fafc" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Real examples (Terminal 5 context)</div>
            <ul style={listStyle}>
              <li>SW1A → T5, 48 hours: Taxi often easier; parking savings small vs stress.</li>
              <li>RG / SL postcodes → T5, 2–3 days: Taxi vs parking depends on fatigue; parking may win on cost if timing is tight.</li>
              <li>Late-night arrivals: Taxi reduces risk of overrun + post-flight driving.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Terminal 5 FAQs</div>
            <div style={{ display: "grid", gap: 8 }}>
              {[
                { q: "Is Terminal 5 parking more expensive than other terminals?", a: "Yes, T5 Short Stay is among the highest-priced at Heathrow. This calculator shows current T5 parking vs taxi costs." },
                { q: "How much is Short Stay parking at Terminal 5 per day?", a: "Short Stay T5 is premium-priced; see the live parking total above for your dates." },
                { q: "Is taxi easier than parking at Terminal 5?", a: "Often yes for 24–48 hour trips due to short-stay costs, fatigue, and PCN risk. The calculator shows your exact difference." },
                { q: "What happens if my BA flight is delayed?", a: "Parking overruns increase cost and stress. Taxi avoids post-flight driving and overrun risk." },
                { q: "Is Long Stay practical for Terminal 5?", a: "Long Stay can be cheaper but adds shuttle time. For short trips, taxi may be simpler." },
                { q: "Can taxis drop off directly at Terminal 5?", a: "Yes, taxis drop at T5 forecourt; drop-off fee applies unless exempt." },
                { q: "Is Terminal 5 harder to navigate than T2 or T3?", a: "T5 is larger with longer walks; plan extra time, especially with luggage." }
              ].map(({ q, a }) => (
                <details key={q} className="train-collapsible" open={false}>
                  <summary className="train-summary-bar" style={{ fontWeight: 700, fontSize: 15 }}>{q}</summary>
                  <div className="train-collapsible-body" style={{ fontSize: 14, color: "#1f2937" }}>{a}</div>
                </details>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
            <strong>Plan your Heathrow journey:</strong>
            <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
              <li>🚖 <a href="/heathrow-fare-compare">Compare taxi fares</a></li>
              <li>🚆 <a href="/train-vs-taxi">Train vs taxi</a></li>
              <li>🅿️ <a href="/heathrow-parking-vs-taxi">Parking vs taxi</a></li>
              <li>✈️ <a href="/flight-checker">Check your flight</a></li>
              <li>🕒 <a href="/leave-for-heathrow">When to leave for Heathrow</a></li>
              <li>🧭 <a href="/terminalFinder">Find your terminal</a></li>
              <li>🧳 <a href="/arrival-assistant">Arrival assistant</a></li>
            </ul>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
            Last updated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
          </div>
        </div>
      )}

      <div style={{ marginTop: 30 }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
